import React, { useState, useEffect, useRef } from 'react';
import { Utensils, Car, ShoppingCart, Search, TrendingDown, Clock, Star, MapPin, Tag, ArrowRight, Sparkles } from 'lucide-react';

const SCENES = [
  {
    id: 'food',
    label: 'FoodCompare',
    icon: Utensils,
    accent: 'from-orange-500 via-red-500 to-pink-600',
    accentText: 'text-orange-500',
    accentBg: 'bg-orange-500',
    query: 'Biryani',
    location: 'Koramangala, Bangalore',
    placeholder: 'Search a dish e.g. Biryani',
    badge: 'Swiggy vs Zomato',
    cheaperOn: 'Swiggy',
    savings: 18,
    cheaperPrice: 249,
    pricierPrice: 299,
    results: [
      { name: 'Meghana Foods', dish: 'Chicken Biryani', rating: 4.5, eta: '32 min',
        left: { brand: 'Swiggy', color: 'text-orange-400', price: 249, type: 'exact' },
        right: { brand: 'Zomato', color: 'text-red-400', price: 285, type: 'est.' },
        cheaper: 'left' },
      { name: 'Donne Biryani House', dish: 'Donne Biryani', rating: 4.3, eta: '28 min',
        left: { brand: 'Swiggy', color: 'text-orange-400', price: 219, type: 'exact' },
        right: { brand: 'Zomato', color: 'text-red-400', price: 209, type: 'est.' },
        cheaper: 'right' },
      { name: 'Empire Restaurant', dish: 'Mutton Biryani', rating: 4.4, eta: '38 min',
        left: { brand: 'Swiggy', color: 'text-orange-400', price: 320, type: 'exact' },
        right: { brand: 'Zomato', color: 'text-red-400', price: 349, type: 'est.' },
        cheaper: 'left' },
    ],
    cta: 'Try FoodCompare →',
    href: '/food-compare',
  },
  {
    id: 'ride',
    label: 'RideCompare',
    icon: Car,
    accent: 'from-violet-600 via-purple-600 to-fuchsia-700',
    accentText: 'text-violet-500',
    accentBg: 'bg-violet-600',
    query: 'Koramangala → Whitefield',
    location: '17.2 km · 25 min',
    placeholder: 'Where to?',
    badge: 'Ola vs Uber',
    cheaperOn: 'Ola Mini',
    savings: 24,
    cheaperPrice: 312,
    pricierPrice: 410,
    results: [
      { name: 'Mini', dish: 'Hatchback, 4 seats', rating: null, eta: '4 min',
        left: { brand: 'Ola', color: 'text-green-400', price: 312, type: 'live' },
        right: { brand: 'Uber', color: 'text-zinc-300', price: 358, type: 'live' },
        cheaper: 'left' },
      { name: 'Sedan', dish: 'Premier, 4 seats', rating: null, eta: '6 min',
        left: { brand: 'Ola', color: 'text-green-400', price: 458, type: 'live' },
        right: { brand: 'Uber', color: 'text-zinc-300', price: 425, type: 'live' },
        cheaper: 'right' },
      { name: 'Auto', dish: 'Quick & cheap', rating: null, eta: '3 min',
        left: { brand: 'Ola', color: 'text-green-400', price: 235, type: 'live' },
        right: { brand: 'Uber', color: 'text-zinc-300', price: 248, type: 'live' },
        cheaper: 'left' },
    ],
    cta: 'Try RideCompare →',
    href: '/ride-compare',
  },
  {
    id: 'grocery',
    label: 'GroceryCompare',
    icon: ShoppingCart,
    accent: 'from-yellow-500 via-amber-500 to-orange-600',
    accentText: 'text-amber-500',
    accentBg: 'bg-amber-500',
    query: 'Amul Milk',
    location: 'HSR Layout · 10 min delivery',
    placeholder: 'Search a product e.g. Amul Milk',
    badge: 'Blinkit vs Zepto',
    cheaperOn: 'Zepto',
    savings: 8,
    cheaperPrice: 56,
    pricierPrice: 62,
    results: [
      { name: 'Amul Taaza Toned Milk', dish: '1 L · Dairy', rating: null, eta: '9 min',
        left: { brand: 'Blinkit', color: 'text-yellow-400', price: 62, type: 'est.' },
        right: { brand: 'Zepto', color: 'text-purple-400', price: 56, type: 'est.' },
        cheaper: 'right' },
      { name: 'Amul Gold Full Cream', dish: '500 ml · Dairy', rating: null, eta: '11 min',
        left: { brand: 'Blinkit', color: 'text-yellow-400', price: 38, type: 'est.' },
        right: { brand: 'Zepto', color: 'text-purple-400', price: 35, type: 'est.' },
        cheaper: 'right' },
      { name: 'Amul Slim Milk', dish: '1 L · Dairy', rating: null, eta: '10 min',
        left: { brand: 'Blinkit', color: 'text-yellow-400', price: 65, type: 'est.' },
        right: { brand: 'Zepto', color: 'text-purple-400', price: 68, type: 'est.' },
        cheaper: 'left' },
    ],
    cta: 'Try GroceryCompare →',
    href: '/grocery-compare',
  },
];

const SCENE_DURATION = 9000;

const HeroDemo = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [typed, setTyped] = useState('');
  const [stage, setStage] = useState('typing'); // typing | searching | results
  const timerRef = useRef(null);

  const scene = SCENES[active];

  // Type-out animation + auto-advance
  useEffect(() => {
    if (paused) return;
    setTyped('');
    setStage('typing');
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTyped(scene.query.slice(0, i));
      if (i >= scene.query.length) {
        clearInterval(typeInterval);
        setTimeout(() => setStage('searching'), 400);
        setTimeout(() => setStage('results'), 1400);
      }
    }, 70);

    timerRef.current = setTimeout(() => {
      setActive((a) => (a + 1) % SCENES.length);
    }, SCENE_DURATION);

    return () => {
      clearInterval(typeInterval);
      clearTimeout(timerRef.current);
    };
  }, [active, paused, scene.query]);

  return (
    <div
      className="relative w-full max-w-5xl mx-auto"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-demo"
    >
      {/* Tab pills */}
      <div className="flex items-center justify-center gap-2 mb-4 flex-wrap">
        {SCENES.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === active;
          return (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              className={`group flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                isActive
                  ? `bg-gradient-to-r ${s.accent} text-white shadow-lg scale-105`
                  : 'bg-white/80 text-gray-600 hover:bg-white border border-gray-200'
              }`}
              data-testid={`demo-tab-${s.id}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{s.label}</span>
            </button>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="flex gap-1.5 mb-3 max-w-md mx-auto">
        {SCENES.map((s, i) => (
          <div key={s.id} className="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${s.accent} ${
                i < active ? 'w-full' : i === active ? '' : 'w-0'
              }`}
              style={i === active && !paused ? {
                width: '100%',
                transition: `width ${SCENE_DURATION}ms linear`
              } : i === active && paused ? { width: '50%' } : {}}
            />
          </div>
        ))}
      </div>

      {/* Mock device frame */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-[#09090b] border border-gray-800"
        key={active}
        data-testid={`demo-scene-${scene.id}`}>
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900/95 border-b border-zinc-800">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-zinc-800 text-[10px] text-zinc-500 font-mono truncate text-center">
            medhasvi.ai{scene.href}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-600">
            <Sparkles className="w-3 h-3 text-amber-400" />
            <span>Live</span>
          </div>
        </div>

        {/* Scene content */}
        <div className="p-5 sm:p-7 min-h-[460px] sm:min-h-[440px] relative">
          {/* Header */}
          <div className="flex items-center justify-between mb-5 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${scene.accent} flex items-center justify-center shadow-lg`}>
                <scene.icon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">{scene.label} AI</h3>
                <p className="text-[10px] text-zinc-500 flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" />{scene.location}
                </p>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">{scene.badge}</span>
          </div>

          {/* Search box with type animation */}
          <div className="flex gap-2 mb-5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <div className="w-full h-11 pl-10 pr-3 flex items-center bg-zinc-900/80 border border-zinc-800 rounded-xl text-sm text-white">
                <span className="font-medium">{typed}</span>
                <span className="inline-block w-0.5 h-4 bg-white ml-0.5 animate-pulse" />
              </div>
            </div>
            <button className={`h-11 px-5 rounded-xl bg-gradient-to-r ${scene.accent} text-white text-xs font-bold shadow-lg`}>
              Compare
            </button>
          </div>

          {/* Searching state */}
          {stage === 'searching' && (
            <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
              <div className="w-10 h-10 rounded-full border-2 border-zinc-700 border-t-transparent animate-spin mb-3" />
              <p className="text-sm text-zinc-400">Comparing prices across platforms…</p>
            </div>
          )}

          {/* Best deal banner + results */}
          {stage === 'results' && (
            <>
              <div className="bg-gradient-to-r from-green-950/40 to-emerald-950/40 border border-green-800/30 rounded-xl p-3 mb-3 flex items-center gap-3 animate-slide-up">
                <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center shrink-0">
                  <TrendingDown className="w-4 h-4 text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-green-400 font-medium">Best Deal</p>
                  <p className="text-sm font-bold text-white truncate">Save ₹{scene.pricierPrice - scene.cheaperPrice} on {scene.cheaperOn}</p>
                </div>
                <span className="text-[10px] font-bold bg-green-500/20 text-green-400 px-2 py-1 rounded-full">-{scene.savings}%</span>
              </div>

              <div className="space-y-2">
                {scene.results.map((r, idx) => (
                  <div
                    key={idx}
                    className="bg-zinc-900/40 border border-zinc-800/50 rounded-xl p-3 animate-slide-up"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate">{r.name}</p>
                        <p className="text-[10px] text-zinc-500 truncate flex items-center gap-2">
                          <span>{r.dish}</span>
                          {r.rating && (
                            <span className="flex items-center gap-0.5">
                              <Star className="w-2.5 h-2.5 text-yellow-400 fill-yellow-400" />{r.rating}
                            </span>
                          )}
                          {r.eta && (
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />{r.eta}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-3">
                      <div className={`text-center py-1.5 rounded-lg ${r.cheaper === 'left' ? 'bg-green-950/30 border border-green-800/30' : 'bg-zinc-800/30'}`}>
                        <p className={`text-[9px] font-bold ${r.left.color}`}>{r.left.brand}</p>
                        <p className="text-sm font-bold text-white">₹{r.left.price}</p>
                      </div>
                      <span className="text-[10px] text-zinc-600 font-bold">VS</span>
                      <div className={`text-center py-1.5 rounded-lg ${r.cheaper === 'right' ? 'bg-green-950/30 border border-green-800/30' : 'bg-zinc-800/30'}`}>
                        <p className={`text-[9px] font-bold ${r.right.color}`}>{r.right.brand}</p>
                        <p className="text-sm font-bold text-white">₹{r.right.price}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href={scene.href}
                className={`absolute bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r ${scene.accent} text-white text-xs font-bold shadow-xl hover:scale-105 transition-transform animate-fade-in`}
                data-testid={`demo-cta-${scene.id}`}
              >
                {scene.cta}
              </a>
            </>
          )}

          {/* Watermark */}
          <div className="absolute top-3 right-3 sm:top-4 sm:right-5 flex items-center gap-1 bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[9px] text-white font-semibold">DEMO</span>
          </div>
        </div>
      </div>

      {/* Caption */}
      <p className="text-center text-xs text-gray-500 mt-3">
        Live demo · hover to pause · click a tab to switch tool
      </p>

      {/* Local animations */}
      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slide-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.4s ease-out both; }
        .animate-slide-up { animation: slide-up 0.5s ease-out both; }
      `}</style>
    </div>
  );
};

export default HeroDemo;
